import { useEffect, useState, useMemo } from "react";
import { useHistory } from "react-router-dom";
import { getMultiClientDashboard } from "../../services/dashboardService";
import CommonLoader from "../common/CommonLoader";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  faSearch,
  faFileInvoice,
  faClipboardList,
} from "@fortawesome/free-solid-svg-icons";
import "./MultiClientDashboard.css";

export default function MultiClientDashboard({ token, setAccount }) {
  let history = useHistory();
  const [dashboardData, setDashboardData] = useState({
    loading: false,
    items: [],
  });
  const [currentPage, setCurrentPage] = useState(1);
  const [searchTerm, setSearchTerm] = useState("");
  const itemsPerPage = 8;

  const selectAccount = (acc) => {
    setAccount(acc.trim());
    history.push(`/dashboard/${acc}`);
  };

  useEffect(() => {
    async function loadDashboard() {
      setDashboardData({ ...dashboardData, loading: true });
      let data = await getMultiClientDashboard(token.name);

      // Sort data by account number
      const sortedData = data.sort((a, b) => {
        const nameA = a.accountNumber.toUpperCase();
        const nameB = b.accountNumber.toUpperCase();
        if (nameA < nameB) return -1;
        if (nameA > nameB) return 1;
        return 0;
      });

      // Limit to 50 items if needed
      setDashboardData({
        loading: false,
        items: sortedData,
      });
    }

    loadDashboard();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Filter clients based on search term
  const filteredClients = useMemo(() => {
    return dashboardData.items.filter(
      (client) =>
        client.accountNumber.toLowerCase().includes(searchTerm.toLowerCase()) ||
        client.clientName.toLowerCase().includes(searchTerm.toLowerCase())
    );
  }, [dashboardData.items, searchTerm]);

  // Calculate pagination
  const totalPages = Math.ceil(filteredClients.length / itemsPerPage);
  const currentClients = useMemo(() => {
    const startIndex = (currentPage - 1) * itemsPerPage;
    return filteredClients.slice(startIndex, startIndex + itemsPerPage);
  }, [currentPage, filteredClients, itemsPerPage]);

  const handlePageChange = (newPage) => {
    if (newPage >= 1 && newPage <= totalPages) {
      setCurrentPage(newPage);
      window.scrollTo(0, 0);
    }
  };

  if (dashboardData.loading) {
    return (
      <div className="multi-client-container">
        <div className="loading-container">
          <CommonLoader loading={true} />
        </div>
      </div>
    );
  }

  return (
    <div className="multi-client-container">
      <div className="multi-client-header">
        <h1 className="multi-client-title">Client Accounts</h1>
        <p className="multi-client-subtitle">
          Select a client account to view their details
        </p>
      </div>

      <div className="search-container">
        <div className="field has-addons">
          <div className="control is-expanded has-icons-left">
            <input
              className="search-input"
              type="text"
              placeholder="Search clients by name or account number..."
              value={searchTerm}
              onChange={(e) => {
                setSearchTerm(e.target.value);
                setCurrentPage(1);
              }}
            />
          </div>
        </div>
      </div>

      <div className="client-grid">
        {currentClients.map((client) => (
          <div
            key={client.accountNumber}
            className="client-card"
            onClick={() => selectAccount(client.accountNumber)}
          >
            <div className="client-card-header">
              <div className="client-account-number">
                {client.accountNumber}
                <span>Click to view</span>
              </div>
              <div className="client-name-multi">{client.clientName}</div>
            </div>

            <div className="client-card-content">
              <div className="client-stats">
                <div className="stat-item invoices">
                  <div className="stat-value">{client.openInvoices || 0}</div>
                  <div className="stat-label">
                    <FontAwesomeIcon icon={faFileInvoice} className="mr-1" />
                    Open Invoices
                  </div>
                </div>

                <div className="stat-item orders">
                  <div className="stat-value">{client.openOrders || 0}</div>
                  <div className="stat-label">
                    <FontAwesomeIcon icon={faClipboardList} className="mr-1" />
                    Open Orders
                  </div>
                </div>
              </div>
            </div>
          </div>
        ))}
      </div>

      {filteredClients.length === 0 && !dashboardData.loading && (
        <div className="has-text-centered py-6">
          <div className="empty-state">
            <FontAwesomeIcon
              icon={faSearch}
              size="3x"
              className="has-text-grey-light mb-3"
            />
            <h3 className="title is-4 has-text-grey">No clients found</h3>
            <p className="subtitle is-6 has-text-grey">
              {searchTerm
                ? `No clients match your search for "${searchTerm}"`
                : "No client accounts available"}
            </p>
          </div>
        </div>
      )}

      {totalPages > 1 && (
        <div className="pagination-controls">
          <button
            className="pagination-btn"
            onClick={() => handlePageChange(1)}
            disabled={currentPage === 1}
          >
            First
          </button>

          <button
            className="pagination-btn"
            onClick={() => handlePageChange(currentPage - 1)}
            disabled={currentPage === 1}
          >
            Previous
          </button>

          <span className="pagination-info">
            Page&nbsp;
            <strong>{currentPage}</strong>
            &nbsp;of&nbsp;
            <strong>{totalPages}</strong>
          </span>

          <button
            className="pagination-btn"
            onClick={() => handlePageChange(currentPage + 1)}
            disabled={currentPage === totalPages}
          >
            Next
          </button>

          <button
            className="pagination-btn"
            onClick={() => handlePageChange(totalPages)}
            disabled={currentPage === totalPages}
          >
            Last
          </button>
        </div>
      )}
    </div>
  );
}
